import { useState, useMemo, useCallback } from 'react';
import { SelectedMethodInfo, DeliveryAreaModeGroup, BulkEditMode } from '../BulkEditModal';

const DELIVERY_AREA_MODE_GROUPS: Record<string, DeliveryAreaModeGroup> = {
  all_cities: 'all_areas',
  'listed-states': 'states',
  states_from_list: 'states',
  states_pen_tool: 'states',
  states_from_address_radius: 'states',
  'listed-cities': 'cities',
  cities_from_list: 'cities',
  cities_from_address_radius: 'cities',
  pen_tool: 'cities',
  streets_from_list: 'cities',
  streets_from_address_radius: 'cities',
  zones: 'zones',
  zip_codes: 'zip_codes',
  custom_pen_tool: 'custom',
  custom_from_address_radius: 'custom',
  custom_from_list: 'custom',
};

export const MODE_GROUP_LABELS: Record<DeliveryAreaModeGroup, string> = {
  all_areas: 'Everywhere',
  states: 'By States',
  cities: 'By Cities',
  zones: 'By Zones',
  zip_codes: 'By Zip Codes',
  custom: 'Custom Area',
};

export const SPECIFIC_METHOD_LABELS: Record<string, string> = {
  'listed-states': 'Listed States',
  states_from_list: 'States from List',
  states_pen_tool: 'States Pen Tool',
  states_from_address_radius: 'States from Address Radius',
  'listed-cities': 'Listed Cities',
  cities_from_list: 'Cities from List',
  cities_from_address_radius: 'Cities from Address Radius',
  pen_tool: 'Pen Tool',
  streets_from_list: 'Streets from List',
  streets_from_address_radius: 'Streets from Address Radius',
  zones: 'Zones',
  zip_codes: 'Zip Codes',
  all_cities: 'All Cities',
  custom_pen_tool: 'Custom Pen Tool',
  custom_from_address_radius: 'Custom from Address Radius',
  custom_from_list: 'Custom from List',
};

const LIVE_RATE_CATEGORIES = ['domestic_live_rate', 'international_live_rate'] as const;

export type MethodType = 'domestic' | 'international';

export interface LiveRateConflict {
  liveRateMethods: SelectedMethodInfo[];
  nonLiveRateMethods: SelectedMethodInfo[];
}

// Helper functions
const isLiveRateMethod = (method: SelectedMethodInfo): boolean => {
  const category = method.shippingMethodCategory?.toLowerCase() || '';
  return LIVE_RATE_CATEGORIES.some((liveCategory) => category === liveCategory);
};

const getDeliveryAreaModeGroup = (method: SelectedMethodInfo): DeliveryAreaModeGroup | null => {
  const mode = method.deliveryAreaSelectMethod;
  if (!mode) return null;
  return DELIVERY_AREA_MODE_GROUPS[mode] || null;
};

const getMethodType = (method: SelectedMethodInfo): MethodType => {
  const category = method.shippingMethodCategory?.toLowerCase() || '';
  return category.includes('international') ? 'international' : 'domestic';
};

const getZipCodesOperator = (method: SelectedMethodInfo): 'includes' | 'excludes' | null => {
  const polygon = method.polygonInputValues?.[0];
  return polygon?.zipCodesOperator || null;
};

interface UseBulkEditConflictsProps {
  selectedMethods: SelectedMethodInfo[];
  onDeselectMethods?: (methodIds: string[]) => void;
}

interface UseBulkEditConflictsReturn {
  // Conflict states
  currencyConflict: boolean;
  methodTypeConflict: boolean;
  deliveryAreaModeConflict: boolean;
  specificDeliveryMethodConflict: boolean;
  zipCodesOperatorConflict: boolean;
  liveRateConflict: LiveRateConflict | null;

  // Grouped methods for rendering conflict views
  methodsByCurrency: Record<string, SelectedMethodInfo[]>;
  methodsByType: Record<MethodType, SelectedMethodInfo[]>;
  methodsByDeliveryAreaMode: Partial<Record<DeliveryAreaModeGroup, SelectedMethodInfo[]>>;
  methodsByZipOperator: Record<'includes' | 'excludes', SelectedMethodInfo[]>;
  methodsBySpecificDeliveryMethod: Record<string, SelectedMethodInfo[]>;

  // Utility functions
  getResolvedDeliveryAreaMode: () => DeliveryAreaModeGroup | null;
  resetConflicts: () => void;

  // Mode selection with conflict detection
  checkAndSetConflictsForMode: (mode: BulkEditMode) => boolean;

  // Conflict resolution handlers
  handleSelectLiveRateMethods: () => void;
  handleSelectNonLiveRateMethods: () => void;
  handleSelectCurrency: (currency: string) => void;
  handleSelectMethodType: (methodType: MethodType) => void;
  handleSelectDeliveryAreaMode: (modeGroup: DeliveryAreaModeGroup) => void;
  handleSelectSpecificDeliveryMethod: (specificMethod: string) => void;
  handleSelectZipCodesOperator: (operator: 'includes' | 'excludes') => void;
}

export const useBulkEditConflicts = ({
  selectedMethods,
  onDeselectMethods,
}: UseBulkEditConflictsProps): UseBulkEditConflictsReturn => {
  // Conflict states
  const [currencyConflict, setCurrencyConflict] = useState(false);
  const [methodTypeConflict, setMethodTypeConflict] = useState(false);
  const [deliveryAreaModeConflict, setDeliveryAreaModeConflict] = useState(false);
  const [specificDeliveryMethodConflict, setSpecificDeliveryMethodConflict] = useState(false);
  const [zipCodesOperatorConflict, setZipCodesOperatorConflict] = useState(false);
  const [liveRateConflict, setLiveRateConflict] = useState<LiveRateConflict | null>(null);

  // Grouped methods (memoized)
  const methodsByCurrency = useMemo(() => {
    const grouped: Record<string, SelectedMethodInfo[]> = {};
    selectedMethods.forEach((method) => {
      if (!grouped[method.currency]) {
        grouped[method.currency] = [];
      }
      grouped[method.currency].push(method);
    });
    return grouped;
  }, [selectedMethods]);

  const methodsByType = useMemo(() => {
    const grouped: Record<MethodType, SelectedMethodInfo[]> = { domestic: [], international: [] };
    selectedMethods.forEach((method) => {
      grouped[getMethodType(method)].push(method);
    });
    return grouped;
  }, [selectedMethods]);

  const methodsByDeliveryAreaMode = useMemo(() => {
    const grouped: Partial<Record<DeliveryAreaModeGroup, SelectedMethodInfo[]>> = {};
    selectedMethods.forEach((method) => {
      const modeGroup = getDeliveryAreaModeGroup(method);
      if (modeGroup) {
        if (!grouped[modeGroup]) {
          grouped[modeGroup] = [];
        }
        grouped[modeGroup]!.push(method);
      }
    });
    return grouped;
  }, [selectedMethods]);

  const methodsByZipOperator = useMemo(() => {
    const grouped: Record<'includes' | 'excludes', SelectedMethodInfo[]> = { includes: [], excludes: [] };
    selectedMethods.forEach((method) => {
      const operator = getZipCodesOperator(method);
      if (operator) {
        grouped[operator].push(method);
      }
    });
    return grouped;
  }, [selectedMethods]);

  const methodsBySpecificDeliveryMethod = useMemo(() => {
    const grouped: Record<string, SelectedMethodInfo[]> = {};
    selectedMethods.forEach((method) => {
      const specificMethod = method.deliveryAreaSelectMethod;
      if (specificMethod) {
        if (!grouped[specificMethod]) {
          grouped[specificMethod] = [];
        }
        grouped[specificMethod].push(method);
      }
    });
    return grouped;
  }, [selectedMethods]);

  // Conflict checking functions
  const checkCurrencyConflict = useCallback((): boolean => {
    if (selectedMethods.length === 0) return false;
    return new Set(selectedMethods.map((m) => m.currency)).size > 1;
  }, [selectedMethods]);

  const checkMethodTypeConflict = useCallback((): boolean => {
    if (selectedMethods.length === 0) return false;
    return new Set(selectedMethods.map((m) => getMethodType(m))).size > 1;
  }, [selectedMethods]);

  const checkDeliveryAreaModeConflict = useCallback(
    (methods: SelectedMethodInfo[] = selectedMethods): boolean => {
      if (methods.length === 0) return false;
      const modeGroups = new Set(
        methods.map((m) => getDeliveryAreaModeGroup(m)).filter((g): g is DeliveryAreaModeGroup => g !== null),
      );
      return modeGroups.size > 1;
    },
    [selectedMethods],
  );

  const checkZipCodesOperatorConflict = useCallback(
    (methods: SelectedMethodInfo[] = selectedMethods): boolean => {
      if (methods.length === 0) return false;
      const methodsWithZipCodeMode = methods.filter((m) => getDeliveryAreaModeGroup(m) === 'zip_codes');
      if (methodsWithZipCodeMode.length === 0) return false;
      const operators = new Set(
        methodsWithZipCodeMode
          .map((m) => getZipCodesOperator(m))
          .filter((op): op is 'includes' | 'excludes' => op !== null),
      );
      return operators.size > 1;
    },
    [selectedMethods],
  );

  const checkSpecificDeliveryMethodConflict = useCallback(
    (methods: SelectedMethodInfo[] = selectedMethods): boolean => {
      if (methods.length === 0) return false;
      const specificMethods = new Set(
        methods.map((m) => m.deliveryAreaSelectMethod).filter((m): m is string => m !== undefined),
      );
      return specificMethods.size > 1;
    },
    [selectedMethods],
  );

  const checkLiveRateConflict = useCallback((): LiveRateConflict | null => {
    if (selectedMethods.length === 0) return null;
    const liveRateMethods = selectedMethods.filter(isLiveRateMethod);
    const nonLiveRateMethods = selectedMethods.filter((m) => !isLiveRateMethod(m));
    if (liveRateMethods.length > 0 && nonLiveRateMethods.length > 0) {
      return { liveRateMethods, nonLiveRateMethods };
    }
    if (liveRateMethods.length > 0 && nonLiveRateMethods.length === 0) {
      return { liveRateMethods, nonLiveRateMethods: [] };
    }
    return null;
  }, [selectedMethods]);

  // Utility functions
  const getResolvedDeliveryAreaMode = useCallback((): DeliveryAreaModeGroup | null => {
    if (selectedMethods.length === 0) return null;
    const modeGroups = new Set(
      selectedMethods.map((m) => getDeliveryAreaModeGroup(m)).filter((g): g is DeliveryAreaModeGroup => g !== null),
    );
    if (modeGroups.size === 1) {
      return Array.from(modeGroups)[0];
    }
    return null;
  }, [selectedMethods]);

  const resetConflicts = useCallback(() => {
    setCurrencyConflict(false);
    setMethodTypeConflict(false);
    setDeliveryAreaModeConflict(false);
    setSpecificDeliveryMethodConflict(false);
    setZipCodesOperatorConflict(false);
    setLiveRateConflict(null);
  }, []);

  const deselectMethods = useCallback(
    (methodIds: string[]) => {
      if (onDeselectMethods && methodIds.length > 0) {
        onDeselectMethods(methodIds);
      }
    },
    [onDeselectMethods],
  );

  // Check and set conflicts when mode is selected - returns true if conflict found
  const checkAndSetConflictsForMode = useCallback(
    (mode: BulkEditMode): boolean => {
      if (mode === 'shipping-thresholds' || mode === 'shipping-rates') {
        const liveConflict = checkLiveRateConflict();
        if (liveConflict) {
          setLiveRateConflict(liveConflict);
          return true;
        }
        if (checkCurrencyConflict()) {
          setCurrencyConflict(true);
          return true;
        }
      }
      if (mode === 'ship-to') {
        if (checkMethodTypeConflict()) {
          setMethodTypeConflict(true);
          return true;
        }
        if (checkDeliveryAreaModeConflict()) {
          setDeliveryAreaModeConflict(true);
          return true;
        }
        if (checkSpecificDeliveryMethodConflict()) {
          setSpecificDeliveryMethodConflict(true);
          return true;
        }
        if (checkZipCodesOperatorConflict()) {
          setZipCodesOperatorConflict(true);
          return true;
        }
      }
      resetConflicts();
      return false;
    },
    [
      checkLiveRateConflict,
      checkCurrencyConflict,
      checkMethodTypeConflict,
      checkDeliveryAreaModeConflict,
      checkSpecificDeliveryMethodConflict,
      checkZipCodesOperatorConflict,
      resetConflicts,
    ],
  );

  // Conflict resolution handlers
  const handleSelectLiveRateMethods = useCallback(() => {
    if (!liveRateConflict) return;
    deselectMethods(liveRateConflict.nonLiveRateMethods.map((m) => m.id));
    setLiveRateConflict({ liveRateMethods: liveRateConflict.liveRateMethods, nonLiveRateMethods: [] });
  }, [liveRateConflict, deselectMethods]);

  const handleSelectNonLiveRateMethods = useCallback(() => {
    if (!liveRateConflict) return;
    deselectMethods(liveRateConflict.liveRateMethods.map((m) => m.id));
    setLiveRateConflict(null);
    const remainingMethods = selectedMethods.filter((m) => !isLiveRateMethod(m));
    if (remainingMethods.length > 0 && new Set(remainingMethods.map((m) => m.currency)).size > 1) {
      setCurrencyConflict(true);
    }
  }, [liveRateConflict, deselectMethods, selectedMethods]);

  const handleSelectCurrency = useCallback(
    (currency: string) => {
      deselectMethods(selectedMethods.filter((m) => m.currency !== currency).map((m) => m.id));
      setCurrencyConflict(false);
    },
    [deselectMethods, selectedMethods],
  );

  const handleSelectMethodType = useCallback(
    (methodType: MethodType) => {
      deselectMethods(selectedMethods.filter((m) => getMethodType(m) !== methodType).map((m) => m.id));
      setMethodTypeConflict(false);
      const remainingMethods = selectedMethods.filter((m) => getMethodType(m) === methodType);
      if (checkDeliveryAreaModeConflict(remainingMethods)) {
        setDeliveryAreaModeConflict(true);
        return;
      }
      if (checkSpecificDeliveryMethodConflict(remainingMethods)) {
        setSpecificDeliveryMethodConflict(true);
        return;
      }
      if (checkZipCodesOperatorConflict(remainingMethods)) {
        setZipCodesOperatorConflict(true);
      }
    },
    [
      deselectMethods,
      selectedMethods,
      checkDeliveryAreaModeConflict,
      checkSpecificDeliveryMethodConflict,
      checkZipCodesOperatorConflict,
    ],
  );

  const handleSelectDeliveryAreaMode = useCallback(
    (modeGroup: DeliveryAreaModeGroup) => {
      deselectMethods(selectedMethods.filter((m) => getDeliveryAreaModeGroup(m) !== modeGroup).map((m) => m.id));
      setDeliveryAreaModeConflict(false);
      const remainingMethods = selectedMethods.filter((m) => getDeliveryAreaModeGroup(m) === modeGroup);
      if (checkSpecificDeliveryMethodConflict(remainingMethods)) {
        setSpecificDeliveryMethodConflict(true);
        return;
      }
      if (modeGroup === 'zip_codes') {
        if (checkZipCodesOperatorConflict(remainingMethods)) {
          setZipCodesOperatorConflict(true);
        }
      }
    },
    [deselectMethods, selectedMethods, checkSpecificDeliveryMethodConflict, checkZipCodesOperatorConflict],
  );

  const handleSelectSpecificDeliveryMethod = useCallback(
    (specificMethod: string) => {
      deselectMethods(selectedMethods.filter((m) => m.deliveryAreaSelectMethod !== specificMethod).map((m) => m.id));
      setSpecificDeliveryMethodConflict(false);
      const remainingMethods = selectedMethods.filter((m) => m.deliveryAreaSelectMethod === specificMethod);
      if (getDeliveryAreaModeGroup(remainingMethods[0]) === 'zip_codes') {
        if (checkZipCodesOperatorConflict(remainingMethods)) {
          setZipCodesOperatorConflict(true);
        }
      }
    },
    [deselectMethods, selectedMethods, checkZipCodesOperatorConflict],
  );

  const handleSelectZipCodesOperator = useCallback(
    (operator: 'includes' | 'excludes') => {
      deselectMethods(selectedMethods.filter((m) => getZipCodesOperator(m) !== operator).map((m) => m.id));
      setZipCodesOperatorConflict(false);
    },
    [deselectMethods, selectedMethods],
  );

  return {
    // Conflict states
    currencyConflict,
    methodTypeConflict,
    deliveryAreaModeConflict,
    specificDeliveryMethodConflict,
    zipCodesOperatorConflict,
    liveRateConflict,

    // Grouped methods
    methodsByCurrency,
    methodsByType,
    methodsByDeliveryAreaMode,
    methodsByZipOperator,
    methodsBySpecificDeliveryMethod,

    // Utility functions
    getResolvedDeliveryAreaMode,
    resetConflicts,

    // Mode selection with conflict detection
    checkAndSetConflictsForMode,

    // Conflict resolution handlers
    handleSelectLiveRateMethods,
    handleSelectNonLiveRateMethods,
    handleSelectCurrency,
    handleSelectMethodType,
    handleSelectDeliveryAreaMode,
    handleSelectSpecificDeliveryMethod,
    handleSelectZipCodesOperator,
  };
};
